import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ChevronDown, ChevronUp, Leaf } from "lucide-react";

import { useGetCountriesWithAlpha, useGetProduceType } from "../../../../api/static-data";
import { CULTIVATION_TYPES, FARMING_TYPES, FIELD_TYPES } from "../../../../config/constants";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { FormSelect } from "../../../../components/ui/Input/FormSelector/FormSelect";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { Form } from "../../../../components/ui/common/form";
import { validateCharacterLength } from "../../../../utils/utils";
import { useCultivarSectionAccordion } from "../hooks/useCultivarSectionAccordion";

type CultivarFormValues = {
  produceType: string;
  dataType: string;
  originCountry: string;
  farmingType: string;
  farmingField: string;
  region: string;
  latitude: number | null;
  longitude: number | null;
  produceCultivar?: string;
};

const CultivarField = forwardRef(
  (
    {
      currentFormData,
      currentDataType = () => {},
      disabled = false,
      sectionsDisabled = false,
      enableAccodationStatus = false,
      onValidityChange = () => {}
    }: any,
    ref
  ) => {
    const { accordionEnabled, toggleAccordion, headerClassName } = useCultivarSectionAccordion(
      sectionsDisabled,
      enableAccodationStatus
    );
    const [dataType, setDataType] = useState<string>("Supplier");
    const [produceType, setProduceType] = useState<any>();
    const [farmingType, setFarmingType] = useState<string>("Unknown");
    const [fieldType, setFieldType] = useState<string>("Unknown");
    const [country, setCountry] = useState<any>();

    const {
      data: produceTypes,
      isSuccess: isProduceTypesSuccess,
      isLoading: isProduceTypesLoading
    } = useGetProduceType();

    const {
      data: countries,
      isSuccess: isCountriesDataSuccess,
      isLoading: isCountriesDataLoading
    } = useGetCountriesWithAlpha();

    const enableAccodation = toggleAccordion;

    const setCurrentProduceType = () => {
      const option = produceTypes?.find(
        (p: any) => parseInt(p.value) === parseInt(currentFormData?.produceType)
      );
      setProduceType(option?.value);
    };

    const setCurrentCountry = () => {
      const option = countries?.find(
        (c: any) => parseInt(c.value) === parseInt(currentFormData?.originCountry)
      );
      setCountry(option?.value || "");
    };

    const getDefaultCountry = useCallback(() => {
      const denmarkValue = countries?.find((e: any) => e.code === "DK");
      return denmarkValue?.key;
    }, [countries]);

    const values = useMemo(() => {
      const defaultFarmingType = FARMING_TYPES[0].value;
      const defaultProduceType = produceTypes?.[0]?.value;
      const fieldType = FIELD_TYPES?.[0]?.value;
      setDataType(currentFormData?.dataType || "Supplier");
      currentDataType(currentFormData?.dataType || "Supplier");
      setCurrentProduceType();
      setCurrentCountry();
      setFarmingType(currentFormData?.farmingType || defaultFarmingType);
      setFieldType(currentFormData?.farmingField || fieldType);
      setProduceType(
        currentFormData?.produceType != null
          ? String(currentFormData.produceType)
          : defaultProduceType
      );
      setCountry(currentFormData?.originCountry || getDefaultCountry());
      return {
        produceType: currentFormData?.produceType || defaultProduceType,
        dataType: currentFormData?.dataType || "",
        originCountry: currentFormData?.originCountry || getDefaultCountry(),
        farmingType: currentFormData?.farmingType || defaultFarmingType,
        farmingField: currentFormData?.farmingField || fieldType,
        region: currentFormData?.region || "",
        latitude: currentFormData?.latitude || null,
        longitude: currentFormData?.longitude || null,
        produceCultivar: currentFormData?.produceCultivar || ""
      };
    }, [currentFormData, produceTypes, isProduceTypesSuccess, isCountriesDataSuccess]);

    const {
      ...form
    } = useForm<CultivarFormValues>({
      defaultValues: values,
      values,
      mode: "all",
      reValidateMode: "onChange"
    });

    const {
      formState: { errors, isValid },
      getValues,
      trigger
    } = form;

    useImperativeHandle(ref, () => ({
      getCultivationData: async () => {
        const isValid = await trigger();

        if (!isValid) {
          return null;
        }

        let currentCultivationData = getValues();
        currentCultivationData.dataType = dataType;
        currentCultivationData.produceType = produceType;
        currentCultivationData.farmingType = farmingType;
        currentCultivationData.farmingField = fieldType;

        return currentCultivationData;
      }
    }));

    useEffect(() => {
      onValidityChange(isValid);
    }, [isValid, onValidityChange]);

    return (
      <Card className="tw-border-border tw-p-0 tw-gap-0">
        <CardHeader
          className={headerClassName}
          onClick={enableAccodation}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Leaf className="tw-h-4 tw-w-4 tw-text-primary" />
              <span className="tw-text-sm tw-font-semibold tw-text-foreground">Cultivar</span>
            </div>
            {accordionEnabled ? <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" /> : <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />}
          </div>
        </CardHeader>
        {accordionEnabled && (
          <CardContent className="tw-py-3.5 tw-px-3.5 tw-space-y-4">
            <Form {...form}>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                <FormSelect control={form.control} name="dataType" label="Data Type" required placeholder="Select Type" options={CULTIVATION_TYPES} disabled={disabled} onChange={value => { setDataType(value); currentDataType(value); }} />
                <FormSelect control={form.control} name="produceType" label="Produce Type" required placeholder="Select Produce Type" options={produceTypes ?? []} disabled={disabled || isProduceTypesLoading} onChange={setProduceType} />
                {/* Temporarily removed country and region selector as per new requirements, will be added back if needed in future
                <Grid.Column computer={16}>
                  <GeoLocationPicker
                    country={country}
                    countries={countries}
                    setCountry={setCountry}
                    isCountriesDataLoading={isCountriesDataLoading}
                    setValue={setValue}
                    register={register}
                    getValues={getValues}
                  />
                </Grid.Column> */}
                <FormInput control={form.control} name="produceCultivar" label="Produce Cultivar" disabled={disabled} errorMessage="Please enter produce cultivar" rules={{ validate: value => validateCharacterLength(value ?? "", 32, "Produce Cultivar cannot exceed 32 characters") }} />
                <FormSelect control={form.control} name="farmingType" label="Farming Type" placeholder="Select Farming Type" options={FARMING_TYPES} disabled={disabled} onChange={setFarmingType} />
                <FormSelect control={form.control} name="farmingField" label="Field Type" placeholder="Select Field Type" options={FIELD_TYPES} disabled={disabled} onChange={setFieldType} />
              </div>
            </Form>
          </CardContent>
        )}
      </Card>
    );
  }
);

export default CultivarField;
